import { pickAt, isVisible } from './picking';
import { ToolBase, ToolPointer, isClick, ShiftLock } from './tool';
import { Model, faceUsesEdge } from '../core/model';
import { Vec3, vec, add, sub, mul, dot, projectPointOnPlane, AXIS_X, AXIS_Y, AXIS_Z, dominantAxis } from '../core/vec';
import { cloneEntities } from '../core/ops';
import { computeMoveDelta } from '../inference/inference';
import { SNAP_COLORS } from '../render/preview';
import { formatLength } from '../core/units';

const VERTEX_HOVER_PX = 10;
const DRAG_THRESHOLD_PX = 4;
/** Griff liegt nahe einer Objektecke → Eck-/Raster-Fang richtet die Ecke aus (wie Verschieben). */
const CORNER_GRAB_RADIUS_PX = 24;

/** Auswahl-Werkzeug (Leertaste): Klick, Umschalt+Klick, Rubber-Band, Doppelklick.
 *  Ecken (Endpunkte) werden beim Hovern markiert und lassen sich direkt greifen
 *  und ziehen — verbundene Kanten/Flächen folgen, mit Eckpunkt-/Kanten-/Raster-Fang.
 *  ⌘/Strg+Ziehen auf ein Element dupliziert es (liegt es in der Auswahl, wird die
 *  gesamte Auswahl kopiert) und verschiebt die Kopie mit Fang. */
export class SelectTool extends ToolBase {
  readonly id = 'select';
  readonly label = 'Auswahl';
  readonly shortcut = 'Leertaste';
  readonly cursor = 'default';
  readonly hint = 'Ecke ziehen = Punkt verschieben · ⌘/Strg+Ziehen = Kopie · Klicken zum Auswählen · Umschalt+Klick hinzufügen/entfernen · Ziehen = Rahmenauswahl';

  private downX = 0;
  private downY = 0;
  private isDown = false;
  private dragging = false;
  private rubberBand?: HTMLDivElement;

  private hoverVertexId: number | null = null;
  /** Zeitpunkt/Ort des letzten Doppelklicks — folgt ein Klick, ist es ein Dreifachklick. */
  private lastDoubleClick: { t: number; x: number; y: number } | null = null;
  private prevCopyHover = false;
  private dragVertex: { id: number; orig: Vec3 } | null = null;
  private vertexDragging = false;
  private lockedAxis: 0 | 1 | 2 | null = null;
  /** Umschalt beim Ziehen → aktuelle Hauptrichtung sperren (wie SketchUp). */
  private shiftLock = new ShiftLock<0 | 1 | 2>();
  private lastDelta: Vec3 = vec();

  /** ⌘/Strg beim Drücken gehalten + Element unter dem Cursor → Kopie ziehen. */
  private copySource: { edges: Set<number>; faces: Set<number> } | null = null;
  private copyDrag: {
    vertexIds: number[];
    originals: Map<number, Vec3>;
    clonedEdges: Set<number>;
    clonedFaces: Set<number>;
    priorSelection: { edges: Set<number>; faces: Set<number> };
    ref: Vec3;
    handle: Vec3;
    cornerGrab: boolean;
    plane: { point: Vec3; normal: Vec3 };
  } | null = null;

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.isDown = true;
    this.downX = e.x;
    this.downY = e.y;
    this.dragging = false;
    this.vertexDragging = false;
    this.dragVertex = null;
    this.copySource = null;
    // ⌘/Strg+Ziehen: Kopie statt Ecken-Griff/Rahmenauswahl (Klick ohne Zug bleibt Auswahl-Klick)
    if (e.ctrl) {
      const src = this.copyTargetAt(e);
      if (src) {
        this.copySource = src;
        return;
      }
    }
    // Ecke unter dem Cursor greifen statt Rahmen/Klick-Auswahl
    const vertexId = this.findVertexAt(e);
    this.dragVertex = vertexId !== null ? { id: vertexId, orig: this.ctx.model.vertexPosition(vertexId) } : null;
  }

  pointerMove(e: ToolPointer): void {
    // ⌘/Strg+Ziehen: Kopie erzeugen (erst ab Zieh-Schwelle) und verschieben
    if (this.isDown && this.copySource) {
      if (!this.copyDrag) {
        if (Math.hypot(e.x - this.downX, e.y - this.downY) <= DRAG_THRESHOLD_PX) return;
        this.startCopyDrag(e);
      }
      this.moveCopyDrag(e);
      return;
    }

    // Ecken-Drag läuft → Vertex verschieben
    if (this.dragVertex) {
      if (!this.vertexDragging && Math.hypot(e.x - this.downX, e.y - this.downY) > DRAG_THRESHOLD_PX) {
        this.vertexDragging = true;
        this.ctx.beginEdit();
      }
      if (this.vertexDragging) {
        this.moveDraggedVertex(e);
        return;
      }
      this.ctx.preview.clear();
      this.showVertexDot(this.dragVertex.orig);
      return;
    }

    if (this.isDown) {
      if (!this.dragging && Math.hypot(e.x - this.downX, e.y - this.downY) > DRAG_THRESHOLD_PX) {
        this.dragging = true;
      }
      if (this.dragging) this.showRubberBand(this.downX, this.downY, e.x, e.y);
      return;
    }

    // Hover: Ecke markieren und greifbar machen; ⌘/Strg über Element = Kopier-Cursor
    const vertexId = this.findVertexAt(e);
    const copyHover = vertexId === null && e.ctrl && this.copyTargetAt(e) !== null;
    if (vertexId !== this.hoverVertexId || copyHover !== this.prevCopyHover) {
      this.hoverVertexId = vertexId;
      this.prevCopyHover = copyHover;
      this.ctx.setViewportCursor(vertexId !== null ? 'move' : copyHover ? 'copy' : null);
      this.ctx.requestUpdate();
    }
    this.ctx.preview.clear();
    if (vertexId !== null) this.showVertexDot(this.ctx.model.vertexPosition(vertexId));
  }

  /** Kopierquelle für ⌘/Strg+Ziehen: Element unter dem Cursor; liegt es in der
   *  Auswahl, wird die gesamte Auswahl kopiert (sonst nur das Element). */
  private copyTargetAt(e: ToolPointer): { edges: Set<number>; faces: Set<number> } | null {
    const { edgeId, faceId } = pickAt(this.ctx, e);
    if (edgeId === null && faceId === null) return null;
    const sel = this.ctx.selection;
    const inSelection = edgeId !== null ? sel.edges.has(edgeId) : sel.faces.has(faceId!);
    if (inSelection) return { edges: new Set(sel.edges), faces: new Set(sel.faces) };
    const target = this.ctx.model.expandToGroups(edgeId !== null ? [edgeId] : [], faceId !== null ? [faceId] : []);
    return target.edges.size + target.faces.size > 0 ? target : null;
  }

  /** Klont die Quelle, wählt die Kopie und legt Referenzpunkt/Ebene für das Ziehen fest. */
  private startCopyDrag(e: ToolPointer): void {
    this.ctx.beginEdit();
    const cloned = cloneEntities(this.ctx.model, this.copySource!.edges, this.copySource!.faces);
    const priorSelection = this.ctx.selection.snapshot();
    this.ctx.selection.set(cloned.edges, cloned.faces);

    const originals = new Map<number, Vec3>();
    for (const id of this.ctx.model.verticesOf(cloned.edges, cloned.faces)) {
      originals.set(id, this.ctx.model.vertexPosition(id));
    }

    // Referenzpunkt + Bewegungsebene; ohne Fläche: horizontale Ebene auf Referenzhöhe
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: null });
    let handle = snap.point;
    let bestDist = Infinity;
    for (const p of originals.values()) {
      const d = this.ctx.camera.screenDistance(p, e.x, e.y);
      if (d < bestDist) {
        bestDist = d;
        handle = p;
      }
    }
    this.copyDrag = {
      vertexIds: [...originals.keys()],
      originals,
      clonedEdges: cloned.edges,
      clonedFaces: cloned.faces,
      priorSelection,
      ref: snap.point,
      handle,
      cornerGrab: bestDist <= CORNER_GRAB_RADIUS_PX,
      plane: snap.faceId !== undefined && snap.plane ? snap.plane : { point: vec(0, 0, snap.point.z), normal: AXIS_Z }
    };
  }

  /** Verschiebt die Kopie mit Eckpunkt-/Raster-Fang (wie das Verschieben-Werkzeug). */
  private moveCopyDrag(e: ToolPointer): void {
    const d = this.copyDrag!;
    const axisLock = this.lockedAxis ?? this.shiftLock.update(e.shift, () => dominantAxis(this.lastDelta));
    const movePlane = axisLock !== null
      ? { point: d.ref, normal: this.ctx.camera.viewDirection() }
      : d.plane;
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, {
      fallbackPlane: movePlane,
      excludeVertices: new Set(d.vertexIds),
      includeFaces: false
    });
    const delta = computeMoveDelta(snap, d.ref, d.handle, movePlane, axisLock, d.cornerGrab);

    this.lastDelta = delta;
    for (const [id, p] of d.originals) {
      const v = this.ctx.model.vertices.get(id);
      if (v) v.p = add(p, delta);
    }

    this.ctx.preview.clear();
    if (snap.kind !== 'ground') {
      this.ctx.preview.dot(snap.point, SNAP_COLORS[snap.kind] ?? 0x333333, snap.priority >= 4 ? 11 : 8);
    }
    this.ctx.ui.setStatus(
      `Kopie · Δ ${formatLength(delta.x, this.ctx.units())}, ${formatLength(delta.y, this.ctx.units())}, ${formatLength(delta.z, this.ctx.units())}` +
        (axisLock !== null ? ` · ${['rote', 'grüne', 'blaue'][axisLock]} Achse` : '')
    );
    this.ctx.requestUpdate();
  }

  /** Entfernt die Kopien (Esc./Werkzeugwechsel mitten im Ziehen) und stellt die Auswahl wieder her. */
  private discardCopyDrag(): void {
    const d = this.copyDrag;
    this.copySource = null;
    this.copyDrag = null;
    if (!d) return;
    for (const id of d.clonedFaces) this.ctx.model.deleteFace(id);
    for (const id of d.clonedEdges) this.ctx.model.deleteEdge(id);
    this.ctx.model.pruneOrphanVertices();
    this.ctx.selection.restore(d.priorSelection);
    this.ctx.requestUpdate();
  }

  /** Verschiebt den gegriffenen Vertex auf den gesnappten Cursorpunkt. */
  private moveDraggedVertex(e: ToolPointer): void {
    const d = this.dragVertex!;
    const axisLock = this.lockedAxis ?? this.shiftLock.update(e.shift, () => dominantAxis(this.lastDelta));
    // Fang: fremde Eckpunkte/Mittelpunkte/Kanten/Flächen + Raster auf Höhe der Ecke
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, {
      fallbackPlane: { point: d.orig, normal: AXIS_Z },
      excludeVertices: new Set([d.id]),
      // Flächen an der gezogenen Ecke verformen sich mit → kein Fang darauf (sonst driftet die Ecke)
      includeFaces: false
    });

    let delta: Vec3;
    if (axisLock !== null) {
      // Achssperre: freies 3D-Ziel (bildschirmparallele Ebene) auf die Achse projizieren
      const raw = projectPointOnPlane(e.ray.origin, e.ray.dir, d.orig, this.ctx.camera.viewDirection()) ?? snap.point;
      const axis = [AXIS_X, AXIS_Y, AXIS_Z][axisLock]!;
      delta = mul(axis, dot(sub(raw, d.orig), axis));
    } else {
      delta = sub(snap.point, d.orig);
    }

    this.lastDelta = delta;
    const v = this.ctx.model.vertices.get(d.id);
    if (v) v.p = add(d.orig, delta);

    this.ctx.preview.clear();
    if (snap.kind !== 'ground') {
      this.ctx.preview.dot(snap.point, SNAP_COLORS[snap.kind] ?? 0x333333, snap.priority >= 4 ? 11 : 8);
    }
    this.ctx.ui.setStatus(
      `Δ ${formatLength(delta.x, this.ctx.units())}, ${formatLength(delta.y, this.ctx.units())}, ${formatLength(delta.z, this.ctx.units())}` +
        (axisLock !== null ? ` · ${['rote', 'grüne', 'blaue'][axisLock]} Achse` : '')
    );
    this.ctx.requestUpdate();
  }

  pointerUp(e: ToolPointer): void {
    this.shiftLock.reset();
    this.lastDelta = vec();
    const wasDown = this.isDown;
    this.isDown = false;
    if (!wasDown) return;

    // ⌘/Strg+Ziehen abgeschlossen: Kopie und Auswahl bleiben auf der Kopie.
    // Ohne Bewegung (Klick) weiter wie ein normaler Auswahl-Klick.
    if (this.copySource) {
      const dragged = this.copyDrag !== null;
      this.copySource = null;
      this.copyDrag = null;
      this.lockedAxis = null;
      this.ctx.preview.clear();
      this.ctx.ui.setStatus(null);
      this.ctx.requestUpdate();
      if (dragged) return;
    }

    // Ecken-Drag abschließen; ohne Bewegung wie ein normaler Klick behandeln
    if (this.dragVertex) {
      const dragged = this.vertexDragging;
      this.dragVertex = null;
      this.vertexDragging = false;
      this.lockedAxis = null;
      this.ctx.preview.clear();
      this.ctx.ui.setStatus(null);
      this.ctx.requestUpdate();
      if (dragged) return;
    }

    if (this.dragging) {
      this.hideRubberBand();
      this.dragging = false;
      this.selectByBand(e, this.downX, this.downY);
      return;
    }
    if (isClick(this.downX, this.downY, e.x, e.y) && e.button === 0) {
      this.selectByClick(e);
    }
  }

  keyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft') this.lockedAxis = 1;
    else if (e.key === 'ArrowRight') this.lockedAxis = 0;
    else if (e.key === 'ArrowUp') this.lockedAxis = 2;
    else if (e.key === 'ArrowDown') this.lockedAxis = null;
  }

  private selectByClick(e: ToolPointer): void {
    const model = this.ctx.model;
    const { edgeId, faceId } = pickAt(this.ctx, e);
    if (edgeId === null && faceId === null) {
      // Klick ins Leere: Auswahl aufheben und offene Gruppen schließen (wie SketchUp)
      if (!e.shift) this.ctx.selection.clear();
      this.ctx.setActiveGroupId(null);
      this.ctx.ui.setStatus(null);
      this.ctx.requestUpdate();
      return;
    }
    const entity = edgeId !== null ? model.edges.get(edgeId) : model.faces.get(faceId!);

    // Klick außerhalb der offenen Gruppe schließt Ebenen, bis das Element im Kontext liegt
    if (this.closeContextsUntil(entity?.group)) this.ctx.selection.clear();

    // Dreifachklick: alles damit Verbundene wählen (im Kontext)
    const triple = this.lastDoubleClick &&
      performance.now() - this.lastDoubleClick.t < 450 &&
      Math.hypot(e.x - this.lastDoubleClick.x, e.y - this.lastDoubleClick.y) < 6;
    this.lastDoubleClick = null;
    if (triple && model.objectAt(entity?.group)?.kind === 'loose') {
      const all = connectedLoose(model, edgeId !== null ? [edgeId] : [], faceId !== null ? [faceId] : []);
      this.ctx.selection.set(all.edges, all.faces);
      this.ctx.requestUpdate();
      return;
    }

    // Geschlossene Gruppe → als Ganzes wählen; sonst das Einzelelement
    const target = model.expandToGroups(edgeId !== null ? [edgeId] : [], faceId !== null ? [faceId] : []);
    const sel = this.ctx.selection;
    if (e.shift) {
      const allIn = [...target.edges].every((id) => sel.edges.has(id)) && [...target.faces].every((id) => sel.faces.has(id));
      const edges = new Set(sel.edges);
      const faces = new Set(sel.faces);
      for (const id of target.edges) allIn ? edges.delete(id) : edges.add(id);
      for (const id of target.faces) allIn ? faces.delete(id) : faces.add(id);
      sel.set(edges, faces);
    } else {
      sel.set(target.edges, target.faces);
    }
    this.ctx.requestUpdate();
  }

  /** Schließt offene Gruppen, bis Gruppe `g` im Kontext liegt. true, wenn sich der Kontext geändert hat. */
  private closeContextsUntil(g: number | undefined): boolean {
    const model = this.ctx.model;
    let c = model.editContext;
    while (c !== undefined && !model.isInside(g, c)) c = model.parentOf(c);
    if (c === model.editContext) return false;
    this.ctx.setActiveGroupId(c ?? null);
    this.ctx.ui.setStatus(null);
    return true;
  }

  /** Rahmenauswahl wie SketchUp: links→rechts = Fenster (vollständig innen),
   *  rechts→links = Kreuzen (berührt). Geschlossene Gruppen zählen als Ganzes. */
  private selectByBand(e: ToolPointer, x0: number, y0: number): void {
    const model = this.ctx.model;
    const crossing = e.x < x0;
    const minX = Math.min(x0, e.x), maxX = Math.max(x0, e.x);
    const minY = Math.min(y0, e.y), maxY = Math.max(y0, e.y);
    const inRect = (p: { x: number; y: number }) => p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
    const screen = new Map<number, { x: number; y: number }>();
    const camPos = this.ctx.camera.position();
    const fwd = mul(this.ctx.camera.viewDirection(), -1);
    const proj = (vid: number) => {
      let s = screen.get(vid);
      if (!s) {
        const p = model.vertexPosition(vid);
        // Punkte hinter der Kamera werden gespiegelt projiziert → nie „im Rahmen“
        s = dot(sub(p, camPos), fwd) <= 0 ? { x: Infinity, y: Infinity } : this.ctx.camera.projectToScreen(p);
        screen.set(vid, s);
      }
      return s;
    };
    const loopHits = (loop: number[]): boolean => {
      if (!crossing) return loop.every((v) => inRect(proj(v)));
      if (loop.some((v) => inRect(proj(v)))) return true;
      for (let i = 0; i < loop.length; i++) {
        if (segmentHitsRect(proj(loop[i]!), proj(loop[(i + 1) % loop.length]!), minX, minY, maxX, maxY)) return true;
      }
      return false;
    };

    const edges: number[] = [];
    const faces: number[] = [];
    // Pro geschlossener Gruppe des Kontexts: getroffen (Kreuzen) / vollständig getroffen (Fenster)
    const groupHit = new Map<number, boolean>();
    const note = (group: number | undefined, hit: boolean): boolean => {
      const o = model.objectAt(group);
      if (!o) return false;
      if (o.kind === 'loose') return true;
      const prev = groupHit.get(o.id);
      groupHit.set(o.id, crossing ? (prev ?? false) || hit : (prev ?? true) && hit);
      return false;
    };
    for (const edge of model.edges.values()) {
      if (!model.isEntityVisible(edge.group)) continue;
      const hit = loopHits([edge.a, edge.b]);
      if (note(edge.group, hit) && hit) edges.push(edge.id);
    }
    // Kreuzen: auch eine Fläche, die den ganzen Rahmen umschließt, wird berührt
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    const enclosesRect = (loop: number[]) => {
      const poly = loop.map((v) => proj(v));
      if (poly.some((q) => !Number.isFinite(q.x))) return false;
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i]!, b = poly[j]!;
        if ((a.y > cy) !== (b.y > cy) && cx < ((b.x - a.x) * (cy - a.y)) / (b.y - a.y + Number.EPSILON) + a.x) inside = !inside;
      }
      return inside;
    };
    for (const face of model.faces.values()) {
      if (!model.isEntityVisible(face.group)) continue;
      const hit = loopHits(face.outer) || (crossing && enclosesRect(face.outer));
      if (note(face.group, hit) && hit) faces.push(face.id);
    }
    for (const [gid, hit] of groupHit) {
      if (!hit) continue;
      const ent = model.entitiesInGroup(gid);
      edges.push(...ent.edges);
      faces.push(...ent.faces);
    }
    if (e.shift) {
      for (const id of edges) if (!this.ctx.selection.edges.has(id)) this.ctx.selection.addEdge(id);
      for (const id of faces) if (!this.ctx.selection.faces.has(id)) this.ctx.selection.addFace(id);
    } else {
      this.ctx.selection.set(edges, faces);
    }
    this.ctx.requestUpdate();
  }

  doubleClick(e: ToolPointer): void {
    const model = this.ctx.model;
    const { edgeId, faceId } = pickAt(this.ctx, e);
    if (edgeId === null && faceId === null) return;
    const entity = edgeId !== null ? model.edges.get(edgeId) : model.faces.get(faceId!);
    if (!entity) return;
    this.lastDoubleClick = { t: performance.now(), x: e.x, y: e.y };

    this.closeContextsUntil(entity.group);
    // Doppelklick auf geschlossene Gruppe: Gruppe öffnen (bearbeiten), nichts wählen
    const o = model.objectAt(entity.group);
    if (o?.kind === 'group') {
      this.ctx.selection.clear();
      this.ctx.setActiveGroupId(o.id);
      this.ctx.ui.setStatus('Gruppe bearbeiten · Klick außerhalb oder Esc schließt die Gruppe');
      this.ctx.requestUpdate();
      return;
    }

    // Im Kontext: Fläche → Fläche + Randkanten, Kante → Kante + angrenzende Flächen
    this.ctx.selection.clear();
    if (faceId !== null) {
      const face = model.faces.get(faceId)!;
      this.ctx.selection.addFace(faceId);
      for (const loop of [face.outer, ...face.holes]) {
        for (let i = 0; i < loop.length; i++) {
          const edge = model.findEdgeBetween(loop[i]!, loop[(i + 1) % loop.length]!);
          if (edge) this.ctx.selection.addEdge(edge.id);
        }
      }
    } else {
      const edge = model.edges.get(edgeId!)!;
      this.ctx.selection.addEdge(edge.id);
      for (const f of model.faces.values()) {
        if (f.group === model.editContext && faceUsesEdge(f, edge.a, edge.b)) this.ctx.selection.addFace(f.id);
      }
    }
    this.ctx.requestUpdate();
  }

  /** Nur laufendes Ziehen abbrechen (Ecke, Kopie, Rahmen) — Auswahl und Gruppe bleiben. */
  abort(): void {
    if (this.vertexDragging && this.dragVertex) {
      const v = this.ctx.model.vertices.get(this.dragVertex.id);
      if (v) v.p = this.dragVertex.orig;
    }
    this.discardCopyDrag();
    this.dragVertex = null;
    this.vertexDragging = false;
    this.copySource = null;
    this.lockedAxis = null;
    this.shiftLock.reset();
    this.hideRubberBand();
    this.isDown = false;
    this.dragging = false;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  cancel(): void {
    // Esc mitten im Ziehen: nur das Ziehen abbrechen (wie SketchUp)
    if (this.isDown || this.vertexDragging || this.copyDrag) {
      this.abort();
      return;
    }
    if (this.vertexDragging && this.dragVertex) {
      // Esc beim Ziehen: Ecke zurücksetzen
      const v = this.ctx.model.vertices.get(this.dragVertex.id);
      if (v) v.p = this.dragVertex.orig;
      this.ctx.requestUpdate();
    }
    // Esc während ⌘/Strg+Ziehen: Kopien wieder entfernen
    this.discardCopyDrag();
    this.dragVertex = null;
    this.vertexDragging = false;
    this.lockedAxis = null;
    // Esc in offener Gruppe: eine Ebene schließen und die Gruppe als Ganzes wählen
    const ctxGroup = this.ctx.activeGroupId();
    if (ctxGroup !== null) {
      this.ctx.setActiveGroupId(this.ctx.model.parentOf(ctxGroup) ?? null);
      const { edges, faces } = this.ctx.model.entitiesInGroup(ctxGroup);
      this.ctx.selection.set(edges, faces);
    } else {
      this.ctx.selection.clear();
    }
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }

  private showVertexDot(p: Vec3): void {
    this.ctx.preview.dot(p, SNAP_COLORS.endpoint ?? 0x2fa04a, 11);
  }

  /** Nächster Vertex zum Cursor in Bildschirm-Pixeln, null wenn außerhalb des Radius. */
  private findVertexAt(e: ToolPointer, radiusPx = VERTEX_HOVER_PX): number | null {
    const { x, y } = e;
    let bestId: number | null = null;
    let bestDist = Infinity;
    const editable = this.ctx.model.contextVertexIds();
    // Ecken hinter der vordersten Fläche sind nicht greifbar
    const hit = this.ctx.renderer.pickFaceHit(e.ndcX, e.ndcY, this.ctx.camera.camera);
    const front = hit?.distance ?? Infinity;
    const hitFace = hit ? this.ctx.model.faces.get(hit.faceId) : undefined;
    const onFront = new Set(hitFace ? [...hitFace.outer, ...hitFace.holes.flat()] : []);
    for (const v of this.ctx.model.vertices.values()) {
      if (!editable.has(v.id)) continue;
      if (!onFront.has(v.id) && !isVisible(e.ray, v.p, front)) continue;
      const d = this.ctx.camera.screenDistance(v.p, x, y);
      if (d < bestDist) {
        bestDist = d;
        bestId = v.id;
      }
    }
    return bestDist <= radiusPx ? bestId : null;
  }

  private showRubberBand(x0: number, y0: number, x1: number, y1: number): void {
    if (!this.rubberBand) {
      this.rubberBand = document.createElement('div');
      this.rubberBand.className = 'rubber-band';
      document.body.appendChild(this.rubberBand);
    }
    // Rechts→links = Kreuzen (gestrichelt), links→rechts = Fenster (durchgezogen)
    this.rubberBand.classList.toggle('crossing', x1 < x0);
    // x/y sind relativ zur Arbeitsfläche; der Rahmen ist fixed → Canvas-Versatz addieren
    const rect = this.ctx.scene.canvasRect();
    Object.assign(this.rubberBand.style, {
      display: 'block',
      left: `${rect.left + Math.min(x0, x1)}px`,
      top: `${rect.top + Math.min(y0, y1)}px`,
      width: `${Math.abs(x1 - x0)}px`,
      height: `${Math.abs(y1 - y0)}px`
    });
  }

  private hideRubberBand(): void {
    this.rubberBand?.remove();
    this.rubberBand = undefined;
  }

  exit(): void {
    if (this.vertexDragging && this.dragVertex) {
      const v = this.ctx.model.vertices.get(this.dragVertex.id);
      if (v) v.p = this.dragVertex.orig;
    }
    // Werkzeugwechsel mitten im ⌘/Strg+Ziehen: Kopien wieder entfernen
    this.discardCopyDrag();
    this.dragVertex = null;
    this.vertexDragging = false;
    this.lockedAxis = null;
    this.hoverVertexId = null;
    this.prevCopyHover = false;
    this.ctx.preview.clear();
    this.ctx.setViewportCursor(null);
    this.hideRubberBand();
    this.isDown = false;
    this.dragging = false;
  }
}


/** true, wenn die Bildschirmstrecke a–b das Rechteck schneidet (Kreuzen-Auswahl). */
function segmentHitsRect(
  a: { x: number; y: number },
  b: { x: number; y: number },
  minX: number, minY: number, maxX: number, maxY: number
): boolean {
  // Punkt hinter der Kamera (unendlich projiziert) → nicht werten
  if (!Number.isFinite(a.x + a.y + b.x + b.y)) return false;
  // Liang-Barsky-Clipping
  let t0 = 0, t1 = 1;
  const dx = b.x - a.x, dy = b.y - a.y;
  const checks: [number, number][] = [[-dx, a.x - minX], [dx, maxX - a.x], [-dy, a.y - minY], [dy, maxY - a.y]];
  for (const [p, q] of checks) {
    if (p === 0) {
      if (q < 0) return false;
    } else {
      const t = q / p;
      if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; }
      else { if (t < t0) return false; if (t < t1) t1 = t; }
    }
  }
  return true;
}

/** Alle über Eckpunkte verbundenen losen Kanten/Flächen des Kontexts (Dreifachklick). */
function connectedLoose(model: Model, edgeIds: number[], faceIds: number[]): { edges: Set<number>; faces: Set<number> } {
  const ctx = model.editContext;
  const loopVerts = (f: { outer: number[]; holes: number[][] }) => [...f.outer, ...f.holes.flat()];
  const verts = new Set(model.verticesOf(edgeIds, faceIds));
  const edges = new Set<number>();
  const faces = new Set<number>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const e of model.edges.values()) {
      if (e.group !== ctx || edges.has(e.id)) continue;
      if (verts.has(e.a) || verts.has(e.b)) {
        edges.add(e.id);
        verts.add(e.a); verts.add(e.b);
        grew = true;
      }
    }
  }
  for (const f of model.faces.values()) {
    if (f.group === ctx && loopVerts(f).some((v) => verts.has(v))) faces.add(f.id);
  }
  return { edges, faces };
}
